import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTenants } from "../../../api/useTenants";
import { createPlatformTenant, putPlatformTenant, resetPlatformTenantSuperAdminPassword, type PlatformTenantPatchBody } from "../../../api/platformTenant";
import { uploadPlatformImageToOss } from "../../../api/platformLibrary";
import { postPlatformTenantWeappUnlimitedQrcode } from "../../../api/platformTenantWeappQrcode";
import { postPlatformClientOperationLog } from "../../../api/platformOperationLogs";
import { statusConfig } from "../../../api/platformUi";
import { usePlanTemplatesMerged } from "../../../api/usePlanTemplatesMerged";
import type { TenantDto, TenantPlan, TenantStatus } from "../../../api/types";
import { platformImpersonation } from "../utils/platformImpersonation";
import { logStore } from "../utils/operationLogStore";
import { getUserFacingMessage } from "../../../api/errors";
import { createTenantWeappPosterDataUrl, dataUrlToBlob } from "../../../utils/tenantWeappPoster";
import ChinaRegionPicker from "../../../components/ChinaRegionPicker";
import { emptyChinaRegion, regionFromNames, type ChinaRegionValue } from "../../../utils/chinaAddress";

/* ─── credential card ─── */
interface Credential {
  tenantName: string;
  /** 租户管理端超级管理员登录账号：租户编号（Tenant.LegacyId） */
  account: string;
  /** 租户代码（Tenant.Code），也可作为登录账号输入（后端兼容） */
  code?: string;
  /** 管理员联系邮箱（仅用于联系，不作为登录账号） */
  contactEmail?: string;
  password: string;
  isReset?: boolean;
}

function CredentialCard({ cred, onClose }: { cred: Credential; onClose: () => void }) {
  const [copiedField, setCopiedField] = useState<"account" | "email" | "pwd" | "all" | null>(null);

  const copy = useCallback(async (text: string, field: "account" | "email" | "pwd" | "all") => {
    const payload = String(text ?? "");
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        const el = document.createElement("textarea");
        el.value = payload;
        el.setAttribute("readonly", "true");
        el.style.position = "fixed";
        el.style.left = "-9999px";
        el.style.top = "-9999px";
        document.body.appendChild(el);
        el.focus();
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // ignore: some environments block clipboard writes
    }
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-2xl w-[460px] overflow-hidden">
        {/* header */}
        <div className={`px-6 pt-6 pb-5 ${cred.isReset ? "bg-blue-50" : "bg-green-50"}`}>
          <div className="flex items-center gap-3 mb-1">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cred.isReset ? "bg-blue-500" : "bg-green-500"}`}>
              <i className={`text-white text-lg ${cred.isReset ? "ri-key-2-line" : "ri-shield-check-line"}`}></i>
            </div>
            <div>
              <p className={`text-sm font-bold ${cred.isReset ? "text-blue-700" : "text-green-700"}`}>
                {cred.isReset ? "密码已重置" : "租户开通成功"}
              </p>
              <p className="text-xs text-gray-500">{cred.tenantName}</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          {/* warning */}
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
            <i className="ri-alarm-warning-line text-amber-500 text-sm mt-0.5 flex-shrink-0"></i>
            <p className="text-xs text-amber-700 leading-relaxed">
              凭证仅展示一次，请立即复制并安全传达给管理员。关闭后将无法再次查看。
            </p>
          </div>

          {/* credential rows */}
          <div className="space-y-3 mb-4">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-400 mb-1">租户管理端登录账号（租户编号 / 租户代码均可）</p>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-mono text-gray-800 truncate">{cred.account}</span>
                <button
                  onClick={() => copy(cred.account, "account")}
                  className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-100 cursor-pointer transition-colors whitespace-nowrap"
                >
                  {copiedField === "account"
                    ? <><i className="ri-check-line text-green-500"></i> 已复制</>
                    : <><i className="ri-file-copy-line"></i> 复制</>
                  }
                </button>
              </div>
              {cred.code ? (
                <p className="mt-1 text-[11px] text-gray-400">租户代码：<span className="font-mono text-gray-500">{cred.code}</span></p>
              ) : null}
            </div>

            {cred.contactEmail ? (
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-400 mb-1">管理员联系邮箱（用于通知/联系，不作为登录账号）</p>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-mono text-gray-800 truncate">{cred.contactEmail}</span>
                  <button
                    onClick={() => copy(cred.contactEmail || "", "email")}
                    className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-100 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    {copiedField === "email"
                      ? <><i className="ri-check-line text-green-500"></i> 已复制</>
                      : <><i className="ri-file-copy-line"></i> 复制</>
                    }
                  </button>
                </div>
              </div>
            ) : null}

            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-400 mb-1">初始密码</p>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-mono tracking-widest text-gray-800">{cred.password}</span>
                <button
                  onClick={() => copy(cred.password, "pwd")}
                  className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-100 cursor-pointer transition-colors whitespace-nowrap"
                >
                  {copiedField === "pwd"
                    ? <><i className="ri-check-line text-green-500"></i> 已复制</>
                    : <><i className="ri-file-copy-line"></i> 复制</>
                  }
                </button>
              </div>
            </div>
          </div>

          {/* copy all */}
          <button
            onClick={() => copy(`租户编号（账号）：${cred.account}\n密码：${cred.password}`, "all")}
            className="w-full py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-xs text-gray-500 hover:border-gray-300 hover:text-gray-700 cursor-pointer transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap"
          >
            {copiedField === "all"
              ? <><i className="ri-check-double-line text-green-500"></i> 已复制全部凭证</>
              : <><i className="ri-clipboard-line"></i> 一键复制全部凭证</>
            }
          </button>
        </div>

        <div className="flex justify-end px-6 pb-6">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-800 text-white text-sm rounded-xl hover:bg-gray-700 cursor-pointer whitespace-nowrap"
          >
            我已保存，关闭
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── main component ─── */
export default function TenantManager() {
  const navigate = useNavigate();
  const { tenants, reload } = useTenants();
  const { mergedByPlan } = usePlanTemplatesMerged();
  const [selectedTenant, setSelectedTenant] = useState<TenantDto | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState<"all" | TenantStatus>("all");
  const [filterPlan, setFilterPlan] = useState<"all" | TenantPlan>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showOnboard, setShowOnboard] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ tenant: TenantDto; action: "suspend" | "activate" | "delete" } | null>(null);
  const [confirmResetPassword, setConfirmResetPassword] = useState<TenantDto | null>(null);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<"info" | "features" | "ops">("info");
  const [savingFeatureFlag, setSavingFeatureFlag] = useState(false);
  const [credential, setCredential] = useState<Credential | null>(null);

  const [editForm, setEditForm] = useState({
    name: "",
    shortName: "",
    code: "",
    region: emptyChinaRegion(),
    sport: "足球",
    adminName: "",
    adminEmail: "",
    adminPhone: "",
    logoColor: "bg-gray-600",
    weChatPaySubMchId: "",
    parentTenantId: "",
    certNumber: "",
    registrationCertImageUrl: "",
  });

  /* batch activation */
  const [batchMode, setBatchMode] = useState(false);
  const [batchSelected, setBatchSelected] = useState<Set<string>>(new Set());
  const [batchConfirm, setBatchConfirm] = useState(false);

  const [weappQr, setWeappQr] = useState<{
    open: boolean;
    tenant: TenantDto | null;
    url: string;
    page: string;
    scene: string;
    envVersion: string;
    busy: boolean;
    err: string | null;
    /** 合成海报预览（含租户名称），与小程序推广海报风格一致 */
    posterUrl: string | null;
    posterBusy: boolean;
  }>({
    open: false,
    tenant: null,
    url: "",
    page: "",
    scene: "",
    envVersion: "",
    busy: false,
    err: null,
    posterUrl: null,
    posterBusy: false,
  });

  const [weappQrSaveTip, setWeappQrSaveTip] = useState<string | null>(null);

  const closeWeappQrModal = useCallback(() => {
    setWeappQrSaveTip(null);
    setWeappQr({
      open: false,
      tenant: null,
      url: "",
      page: "",
      scene: "",
      envVersion: "",
      busy: false,
      err: null,
      posterUrl: null,
      posterBusy: false,
    });
  }, []);

  const [newTenant, setNewTenant] = useState({
    name: "", shortName: "", region: emptyChinaRegion(), sport: "足球",
    adminName: "", adminEmail: "", adminPhone: "", plan: "standard" as TenantPlan,
    expiresAt: "",
    weChatPaySubMchId: "",
    parentTenantId: "",
    certNumber: "",
    registrationCertImageUrl: "",
  });

  const [certUploadBusy, setCertUploadBusy] = useState<"edit" | "onboard" | null>(null);

  const pendingCount = tenants.filter((t) => t.status === "pending").length;

  const uploadCertImage = useCallback(
    async (file: File | null, target: "edit" | "onboard") => {
      if (!file) return;
      setCertUploadBusy(target);
      setApiError(null);
      try {
        const url = await uploadPlatformImageToOss(file);
        if (target === "edit") setEditForm((p) => ({ ...p, registrationCertImageUrl: url }));
        else setNewTenant((p) => ({ ...p, registrationCertImageUrl: url }));
      } catch (e) {
        setApiError(getUserFacingMessage(e, "证书图片上传失败，请检查 OSS 配置与网络。"));
      } finally {
        setCertUploadBusy(null);
      }
    },
    [],
  );

  const handleGenerateTenantWeappQr = useCallback(async (tenant: TenantDto) => {
    setWeappQrSaveTip(null);
    setWeappQr((p) => ({
      ...p,
      open: true,
      tenant,
      url: "",
      page: "",
      scene: "",
      envVersion: "",
      busy: true,
      err: null,
      posterUrl: null,
      posterBusy: false,
    }));
    try {
      const r = await postPlatformTenantWeappUnlimitedQrcode(tenant.id, { page: "pages/landing/index", envVersion: "release", width: 430 });
      setWeappQr((p) => ({
        ...p,
        url: String(r?.url || "").trim(),
        page: String(r?.page || "").trim(),
        scene: String(r?.scene || "").trim(),
        envVersion: String(r?.envVersion || "").trim(),
        busy: false,
        err: null,
        posterUrl: null,
        posterBusy: true,
      }));
    } catch (e) {
      setWeappQr((p) => ({
        ...p,
        busy: false,
        err: getUserFacingMessage(e, "生成失败，请检查小程序配置/OSS/网络后重试。"),
        posterUrl: null,
        posterBusy: false,
      }));
    }
  }, []);

  useEffect(() => {
    if (!weappQr.open || !weappQr.url || !weappQr.tenant?.name) return;
    const qrUrl = weappQr.url;
    const tenantName = weappQr.tenant.name;
    let cancelled = false;
    void (async () => {
      try {
        const dataUrl = await createTenantWeappPosterDataUrl(qrUrl, tenantName);
        if (!cancelled) setWeappQr((p) => ({ ...p, posterUrl: dataUrl, posterBusy: false }));
      } catch {
        if (!cancelled) setWeappQr((p) => ({ ...p, posterUrl: null, posterBusy: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [weappQr.open, weappQr.url, weappQr.tenant?.name]);

  const filtered = tenants
    .filter((t) => filterStatus === "all" || t.status === filterStatus)
    .filter((t) => filterPlan === "all" || t.plan === filterPlan)
    .filter((t) =>
      !searchQuery ||
      t.name.includes(searchQuery) ||
      t.code.includes(searchQuery) ||
      t.city.includes(searchQuery) ||
      t.adminEmail.includes(searchQuery)
    );

  useEffect(() => {
    if (!selectedTenant) return;
    const fresh = tenants.find((t) => t.id === selectedTenant.id);
    if (fresh) setSelectedTenant(fresh);
    else setSelectedTenant(null);
  }, [tenants, selectedTenant?.id]);

  useEffect(() => {
    if (!selectedTenant) return;
    setEditForm({
      name: selectedTenant.name,
      shortName: selectedTenant.shortName,
      code: selectedTenant.code,
      region: regionFromNames(
        selectedTenant.province,
        selectedTenant.city,
        selectedTenant.district ?? "",
      ),
      sport: selectedTenant.sport,
      adminName: selectedTenant.adminName,
      adminEmail: selectedTenant.adminEmail,
      adminPhone: selectedTenant.adminPhone || "",
      logoColor: selectedTenant.logoColor,
      weChatPaySubMchId: selectedTenant.weChatPaySubMchId ?? "",
      parentTenantId: selectedTenant.parentTenantId ?? "",
      certNumber: selectedTenant.certNumber ?? "",
      registrationCertImageUrl: selectedTenant.registrationCertImageUrl ?? "",
    });
  }, [
    selectedTenant?.id,
    selectedTenant?.name,
    selectedTenant?.shortName,
    selectedTenant?.code,
    selectedTenant?.city,
    selectedTenant?.province,
    selectedTenant?.district,
    selectedTenant?.regionCode,
    selectedTenant?.sport,
    selectedTenant?.adminName,
    selectedTenant?.adminEmail,
    selectedTenant?.adminPhone,
    selectedTenant?.logoColor,
    selectedTenant?.weChatPaySubMchId,
    selectedTenant?.parentTenantId,
    selectedTenant?.certNumber,
    selectedTenant?.registrationCertImageUrl,
  ]);

  const handleToggleStatus = async (tenant: TenantDto, action: "suspend" | "activate") => {
    const newStatus: TenantStatus = action === "suspend" ? "suspended" : "active";
    try {
      setApiError(null);
      setSaving(true);
      await putPlatformTenant(tenant.id, { status: newStatus });
      await reload();
      setConfirmAction(null);
      await logStore.refreshFromApi();
    } catch (e) {
      setApiError(getUserFacingMessage(e, "操作未能完成，请检查后重试。"));
    } finally {
      setSaving(false);
    }
  };

  const handleOnboard = async () => {
    if (!newTenant.name || !newTenant.adminEmail) return;
    try {
      setApiError(null);
      setSaving(true);
      const t = await createPlatformTenant({
        name: newTenant.name,
        shortName: newTenant.shortName || newTenant.name,
        province: newTenant.region.province,
        city: newTenant.region.city,
        district: newTenant.region.district || undefined,
        regionCode: newTenant.region.regionCode || undefined,
        sport: newTenant.sport,
        adminName: newTenant.adminName || undefined,
        adminEmail: newTenant.adminEmail,
        adminPhone: newTenant.adminPhone || undefined,
        plan: newTenant.plan,
        expiresAt: newTenant.expiresAt || undefined,
        weChatPaySubMchId: newTenant.weChatPaySubMchId.trim() || undefined,
        parentTenantId: newTenant.parentTenantId.trim() || undefined,
        certNumber: newTenant.certNumber.trim() || undefined,
        registrationCertImageUrl: newTenant.registrationCertImageUrl.trim() || undefined,
      });
      const pwd = t.generatedSuperAdminPassword;
      if (!pwd) {
        setApiError("服务端未返回初始密码，请在租户详情中点击「重置超级管理员密码」重新生成。");
      }
      await reload();
      setShowOnboard(false);
      setSelectedTenant(t);
      setNewTenant({
        name: "", shortName: "", region: emptyChinaRegion(), sport: "足球",
        adminName: "", adminEmail: "", adminPhone: "", plan: "standard", expiresAt: "",
        weChatPaySubMchId: "",
        parentTenantId: "",
        certNumber: "",
        registrationCertImageUrl: "",
      });
      await logStore.refreshFromApi();
      if (pwd) setCredential({ tenantName: t.name, account: t.id, code: t.code, contactEmail: t.adminEmail, password: pwd });
    } catch (e) {
      setApiError(getUserFacingMessage(e, "操作未能完成，请检查后重试。"));
    } finally {
      setSaving(false);
    }
  };

  const buildTenantPatchFromForm = (tenant: TenantDto): PlatformTenantPatchBody | null => {
    const body: PlatformTenantPatchBody = {};
    if (editForm.name.trim() !== tenant.name) body.name = editForm.name.trim();
    if (editForm.shortName.trim() !== tenant.shortName) body.shortName = editForm.shortName.trim();
    if (editForm.code.trim() !== tenant.code) body.code = editForm.code.trim();
    if (editForm.region.city.trim() !== tenant.city) body.city = editForm.region.city.trim();
    if (editForm.region.province.trim() !== tenant.province) body.province = editForm.region.province.trim();
    if (editForm.region.district.trim() !== (tenant.district ?? "").trim())
      body.district = editForm.region.district.trim();
    if (editForm.region.regionCode.trim() !== (tenant.regionCode ?? "").trim())
      body.regionCode = editForm.region.regionCode.trim();
    if (editForm.sport.trim() !== tenant.sport) body.sport = editForm.sport.trim();
    if (editForm.adminName.trim() !== (tenant.adminName || "")) body.adminName = editForm.adminName.trim();
    if (editForm.adminEmail.trim() !== tenant.adminEmail) body.adminEmail = editForm.adminEmail.trim();
    if (editForm.adminPhone.trim() !== (tenant.adminPhone || "")) body.adminPhone = editForm.adminPhone.trim();
    if (editForm.logoColor.trim() !== tenant.logoColor) body.logoColor = editForm.logoColor.trim();
    if (editForm.weChatPaySubMchId.trim() !== (tenant.weChatPaySubMchId ?? "").trim())
      body.weChatPaySubMchId = editForm.weChatPaySubMchId.trim();
    if (editForm.parentTenantId.trim() !== (tenant.parentTenantId ?? "").trim())
      body.parentTenantId = editForm.parentTenantId.trim();
    if (editForm.certNumber.trim() !== (tenant.certNumber ?? "").trim())
      body.certNumber = editForm.certNumber.trim();
    if (editForm.registrationCertImageUrl.trim() !== (tenant.registrationCertImageUrl ?? "").trim())
      body.registrationCertImageUrl = editForm.registrationCertImageUrl.trim();
    return Object.keys(body).length > 0 ? body : null;
  };

  const handleSaveTenantProfile = async () => {
    if (!selectedTenant) return;
    const patch = buildTenantPatchFromForm(selectedTenant);
    if (!patch) {
      setApiError(null);
      return;
    }
    try {
      setApiError(null);
      setSaving(true);
      const updated = await putPlatformTenant(selectedTenant.id, patch);
      await reload();
      setSelectedTenant(updated);
      await logStore.refreshFromApi();
    } catch (e) {
      setApiError(getUserFacingMessage(e, "保存失败，请检查后重试。"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActivityAiCreate = async (enabled: boolean) => {
    if (!selectedTenant) return;
    try {
      setApiError(null);
      setSavingFeatureFlag(true);
      const updated = await putPlatformTenant(selectedTenant.id, { activityAiCreateEnabled: enabled });
      setSelectedTenant(updated);
      await reload();
      await logStore.refreshFromApi();
    } catch (e) {
      setApiError(getUserFacingMessage(e, "保存 AI 创建权限失败，请稍后重试。"));
    } finally {
      setSavingFeatureFlag(false);
    }
  };

  const openResetPasswordConfirm = (tenant: TenantDto) => {
    setResetPasswordError(null);
    setApiError(null);
    setConfirmResetPassword(tenant);
  };

  const handleResetPassword = async (tenant: TenantDto) => {
    try {
      setResetPasswordError(null);
      setApiError(null);
      setResettingPassword(true);
      setSaving(true);
      const res = await resetPlatformTenantSuperAdminPassword(tenant.id);
      const pwd = res?.generatedSuperAdminPassword;
      if (!pwd) {
        const msg =
          "服务端未返回新密码。若运营后台已更新而接口未更新，请先部署最新后端；否则请重试或联系技术人员。";
        setResetPasswordError(msg);
        setApiError(msg);
        return;
      }
      setConfirmResetPassword(null);
      setResetPasswordError(null);
      setSelectedTenant((prev) => (prev?.id === tenant.id ? res : prev));
      setCredential({
        tenantName: res.name,
        account: res.id,
        code: res.code,
        contactEmail: res.adminEmail,
        password: pwd,
        isReset: true,
      });
      void reload().then(() => logStore.refreshFromApi());
    } catch (e) {
      const msg = getUserFacingMessage(e, "重置密码失败，请稍后重试。");
      setResetPasswordError(msg);
      setApiError(msg);
    } finally {
      setResettingPassword(false);
      setSaving(false);
    }
  };

  const handleEnterTenant = async (tenant: TenantDto) => {
    platformImpersonation.start({
      tenantId: tenant.id,
      tenantName: tenant.name,
      tenantCode: tenant.code,
      logoColor: tenant.logoColor,
    });
    try {
      await postPlatformClientOperationLog({
        actionType: "impersonate",
        targetTenant: tenant.name,
        targetTenantCode: tenant.code,
        detail: `以平台管理员身份进入租户「${tenant.name}」后台查看，已开启模拟身份会话`,
        result: "success",
      });
      await logStore.refreshFromApi();
    } catch {
      /* 日志失败不阻断进入后台 */
    }
    navigate("/admin");
  };

  /* batch logic */
  const toggleBatchMode = () => {
    setBatchMode((v) => !v);
    setBatchSelected(new Set());
  };

  const toggleBatchItem = (id: string, isPending: boolean) => {
    if (!isPending) return;
    setBatchSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllPending = () => {
    const pendingIds = filtered.filter((t) => t.status === "pending").map((t) => t.id);
    setBatchSelected(new Set(pendingIds));
  };

  const handleBatchActivate = async () => {
    try {
      setApiError(null);
      setSaving(true);
      await Promise.all([...batchSelected].map((id) => putPlatformTenant(id, { status: "active" })));
      await reload();
      await logStore.refreshFromApi();
      setBatchSelected(new Set());
      setBatchMode(false);
      setBatchConfirm(false);
    } catch (e) {
      setApiError(getUserFacingMessage(e, "操作未能完成，请检查后重试。"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-800">租户管理</h2>
          <p className="text-xs text-gray-400 mt-0.5">管理所有已入驻平台的球迷协会组织</p>
          {apiError && (
            <p className="text-xs text-red-600 mt-2 max-w-xl">{apiError}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowOnboard(true)}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm rounded-xl hover:bg-amber-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          <i className="ri-add-line"></i> 开通新租户
        </button>
      </div>

      {/* Pending banner */}
      {pendingCount > 0 && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
          <div className="flex items-center gap-2">
            <i className="ri-time-line text-amber-500"></i>
            <span className="text-sm text-amber-700">
              有 <strong>{pendingCount}</strong> 个租户处于「待激活」状态
            </span>
          </div>
          <button
            onClick={toggleBatchMode}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${
              batchMode
                ? "bg-amber-200 text-amber-800 hover:bg-amber-300"
                : "bg-amber-500 text-white hover:bg-amber-400"
            }`}
          >
            <i className={batchMode ? "ri-close-line" : "ri-checkbox-multiple-line"}></i>
            {batchMode ? "退出批量模式" : "批量激活"}
          </button>
        </div>
      )}

      {/* Batch action bar */}
      {batchMode && (
        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3 mb-4">
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">
              已选 <strong className="text-gray-800">{batchSelected.size}</strong> 个待激活租户
            </span>
            <button
              onClick={selectAllPending}
              className="text-xs text-amber-600 hover:text-amber-700 cursor-pointer underline underline-offset-2 whitespace-nowrap"
            >
              全选待激活
            </button>
          </div>
          <button
            onClick={() => { if (batchSelected.size > 0) setBatchConfirm(true); }}
            disabled={batchSelected.size === 0}
            className="flex items-center gap-1.5 px-4 py-2 bg-green-500 text-white text-xs rounded-lg hover:bg-green-400 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            <i className="ri-checkbox-circle-line"></i>
            确认激活选中 ({batchSelected.size}) 个
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48 max-w-64">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索租户名称、代码..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 transition-colors"
          />
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {(["all", "active", "pending", "suspended", "expired"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${
                filterStatus === s ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {s === "all" ? "全部" : statusConfig[s as TenantStatus].label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {(["all", "trial", "basic", "standard", "premium"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setFilterPlan(p)}
              className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${
                filterPlan === p ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {p === "all" ? "全套餐" : mergedByPlan[p as TenantPlan].label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-5">
        {/* TenantDto List */}
        <div className="flex-1 min-w-0 space-y-2">
          {filtered.map((tenant) => {
            const plan = mergedByPlan[tenant.plan];
            const status = statusConfig[tenant.status];
            const mauRate = tenant.memberCount > 0
              ? Math.round((tenant.monthlyActiveUsers / tenant.memberCount) * 100)
              : 0;
            const isPending = tenant.status === "pending";
            const isChecked = batchSelected.has(tenant.id);

            return (
              <div
                key={tenant.id}
                onClick={() => {
                  if (batchMode) { toggleBatchItem(tenant.id, isPending); return; }
                  setSelectedTenant(tenant); setActiveTab("info");
                }}
                className={`bg-white rounded-2xl p-4 cursor-pointer transition-all border-2 ${
                  batchMode && isPending
                    ? isChecked
                      ? "border-green-400 bg-green-50"
                      : "border-amber-200 hover:border-amber-300"
                    : selectedTenant?.id === tenant.id
                      ? "border-amber-400"
                      : "border-transparent hover:border-gray-200"
                } ${tenant.status === "suspended" ? "opacity-60" : ""}`}
              >
                <div className="flex items-start gap-3">
                  {/* batch checkbox */}
                  {batchMode && (
                    <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-2.5 rounded-md border-2 transition-colors ${
                      isPending
                        ? isChecked ? "bg-green-500 border-green-500" : "border-gray-300"
                        : "border-gray-200 opacity-30"
                    }`}>
                      {isChecked && <i className="ri-check-line text-white text-xs"></i>}
                    </div>
                  )}
                  <div className={`w-10 h-10 ${tenant.logoColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <i className="ri-building-4-line text-white"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-bold text-gray-800">{tenant.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${plan.bgColor} ${plan.color} font-medium`}>
                        {plan.label}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full flex items-center gap-1 ${status.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
                      <span className="font-mono">{tenant.code}</span>
                      <span>{tenant.city} · {tenant.sport}</span>
                      <span>会员 {tenant.memberCount.toLocaleString()}</span>
                      {tenant.status === "active" && (
                        <span className={`font-medium ${mauRate >= 60 ? "text-green-600" : "text-amber-600"}`}>
                          月活 {mauRate}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 text-xs text-gray-400">
                    <p>到期</p>
                    <p className="text-gray-600 font-medium">{tenant.expiresAt.slice(0, 7)}</p>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-building-4-line text-4xl text-gray-200 block mb-2"></i>
              <p className="text-sm">未找到匹配的租户</p>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedTenant && !batchMode && (
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-2xl overflow-hidden sticky top-0">
              {/* TenantDto Header */}
              <div className={`${selectedTenant.logoColor} p-5`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white/70 text-xs mb-1">租户详情</p>
                    <h4 className="text-white font-bold text-base leading-tight">{selectedTenant.name}</h4>
                    <p className="text-white/60 text-xs mt-1 font-mono">{selectedTenant.code}</p>
                  </div>
                  <button
                    onClick={() => setSelectedTenant(null)}
                    className="w-7 h-7 flex items-center justify-center text-white/60 hover:text-white cursor-pointer rounded-lg hover:bg-white/10"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-white/20 text-white">
                    {mergedByPlan[selectedTenant.plan].label}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${statusConfig[selectedTenant.status].color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusConfig[selectedTenant.status].dot}`}></span>
                    {statusConfig[selectedTenant.status].label}
                  </span>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-100">
                {([
                  { id: "info", label: "基本信息" },
                  { id: "features", label: "功能权限" },
                  { id: "ops", label: "操作" },
                ] as const).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 py-2.5 text-xs cursor-pointer transition-colors border-b-2 ${
                      activeTab === tab.id
                        ? "border-amber-500 text-amber-600 font-medium"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="p-4">
                {apiError && (
                  <div className="mb-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700">
                    <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />
                    <span>{apiError}</span>
                  </div>
                )}
                {activeTab === "info" && (
                  <div className="space-y-3 text-xs">
                    <p className="text-gray-500 leading-relaxed">
                      修改租户资料与管理员联系信息后点击保存；重置密码将生成新的协会后台超级管理员口令并展示一次。
                    </p>
                    <div className="space-y-2">
                      <label className="block text-gray-400">组织全称</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-gray-800 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-gray-400">组织简称</label>
                      <input
                        type="text"
                        value={editForm.shortName}
                        onChange={(e) => setEditForm((p) => ({ ...p, shortName: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-gray-800 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-gray-400">租户代码</label>
                      <input
                        type="text"
                        value={editForm.code}
                        onChange={(e) => setEditForm((p) => ({ ...p, code: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-2.5 py-2 font-mono text-gray-800 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2 col-span-2">
                        <label className="block text-gray-400">所在地区（省 / 市 / 区县可选）</label>
                        <ChinaRegionPicker
                          value={editForm.region}
                          onChange={(region: ChinaRegionValue) => setEditForm((p) => ({ ...p, region }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-gray-400">运动类型</label>
                      <select
                        value={editForm.sport}
                        onChange={(e) => setEditForm((p) => ({ ...p, sport: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-gray-800 focus:outline-none focus:border-amber-400 bg-white"
                      >
                        {["足球", "篮球", "排球", "棒球", "网球", "其他"].map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-gray-400">管理员姓名</label>
                      <input
                        type="text"
                        value={editForm.adminName}
                        onChange={(e) => setEditForm((p) => ({ ...p, adminName: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-gray-800 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-gray-400">管理员邮箱</label>
                      <input
                        type="email"
                        value={editForm.adminEmail}
                        onChange={(e) => setEditForm((p) => ({ ...p, adminEmail: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-gray-800 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-gray-400">管理员电话</label>
                      <input
                        type="tel"
                        value={editForm.adminPhone}
                        onChange={(e) => setEditForm((p) => ({ ...p, adminPhone: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-gray-800 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-gray-400">主题色 class（Tailwind）</label>
                      <input
                        type="text"
                        value={editForm.logoColor}
                        onChange={(e) => setEditForm((p) => ({ ...p, logoColor: e.target.value }))}
                        placeholder="如 bg-red-600"
                        className="w-full border border-gray-200 rounded-lg px-2.5 py-2 font-mono text-gray-800 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-gray-400">微信支付子商户号（mchid）</label>
                      <p className="text-[11px] text-gray-400 leading-relaxed">
                        协会后台开启「在线支付」入会时使用；与微信特约商户一致，最多 32 字符。
                      </p>
                      <input
                        type="text"
                        value={editForm.weChatPaySubMchId}
                        onChange={(e) => setEditForm((p) => ({ ...p, weChatPaySubMchId: e.target.value }))}
                        placeholder="留空则无法发起入会微信支付"
                        className="w-full border border-gray-200 rounded-lg px-2.5 py-2 font-mono text-gray-800 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-gray-400">上级租户</label>
                      <p className="text-[11px] text-gray-400 leading-relaxed">
                        默认无上级。仅活动报名可与上下级互通，内部管理仍各自独立。
                      </p>
                      <select
                        value={editForm.parentTenantId}
                        onChange={(e) => setEditForm((p) => ({ ...p, parentTenantId: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-gray-800 focus:outline-none focus:border-amber-400 bg-white"
                      >
                        <option value="">无（本租户独立）</option>
                        {tenants
                          .filter((t) => t.id !== selectedTenant?.id)
                          .map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}（{t.id}）
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-gray-400">登记证书编号</label>
                      <p className="text-[11px] text-gray-400 leading-relaxed">
                        社会团体法人登记证书等备案编号；与租户协会后台「基本信息」及会员端展示同源。
                      </p>
                      <input
                        type="text"
                        value={editForm.certNumber}
                        onChange={(e) => setEditForm((p) => ({ ...p, certNumber: e.target.value }))}
                        placeholder="如：XXXX 证字第 ×××× 号"
                        className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-gray-800 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-gray-400">团体登记证书图片</label>
                      <p className="text-[11px] text-gray-400 leading-relaxed">
                        支持上传至平台 OSS，或直接粘贴已托管的图片 URL。
                      </p>
                      {editForm.registrationCertImageUrl.trim() ? (
                        <div className="flex flex-wrap items-start gap-2">
                          <img
                            src={editForm.registrationCertImageUrl.trim()}
                            alt="登记证书预览"
                            className="max-h-28 rounded-lg border border-gray-200 object-contain bg-white"
                          />
                          <button
                            type="button"
                            onClick={() => setEditForm((p) => ({ ...p, registrationCertImageUrl: "" }))}
                            className="text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg border border-red-200 hover:bg-red-50"
                          >
                            清除图片
                          </button>
                        </div>
                      ) : null}
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs border border-gray-200 bg-white text-gray-700 hover:border-amber-300 cursor-pointer disabled:opacity-40">
                          <i className="ri-image-add-line" />
                          {certUploadBusy === "edit" ? "上传中…" : "上传证书扫描件"}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={saving || certUploadBusy !== null}
                            onChange={(e) => void uploadCertImage(e.target.files?.[0] ?? null, "edit")}
                          />
                        </label>
                      </div>
                      <input
                        type="url"
                        value={editForm.registrationCertImageUrl}
                        onChange={(e) => setEditForm((p) => ({ ...p, registrationCertImageUrl: e.target.value }))}
                        placeholder="https://… 图片地址"
                        className="w-full border border-gray-200 rounded-lg px-2.5 py-2 font-mono text-xs text-gray-800 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                    <button
                      type="button"
                      disabled={saving || !buildTenantPatchFromForm(selectedTenant)}
                      onClick={() => void handleSaveTenantProfile()}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-500 text-white rounded-xl hover:bg-amber-400 cursor-pointer font-medium disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      <i className="ri-save-line" />
                      保存资料变更
                    </button>
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-400 mb-2">账号安全</p>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => openResetPasswordConfirm(selectedTenant)}
                        className="w-full flex items-center justify-center gap-2 p-3 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 cursor-pointer transition-colors text-xs font-medium whitespace-nowrap disabled:opacity-50"
                      >
                        <i className={`${resettingPassword ? "ri-loader-4-line animate-spin" : "ri-key-2-line"}`} />
                        {resettingPassword ? "正在重置…" : "重置超级管理员密码"}
                      </button>
                    </div>
                    <div className="pt-2 border-t border-gray-100 space-y-2">
                      {[
                        { label: "会员总数", value: `${selectedTenant.memberCount.toLocaleString()} 人` },
                        { label: "月活用户", value: selectedTenant.monthlyActiveUsers > 0 ? `${selectedTenant.monthlyActiveUsers.toLocaleString()} 人` : "-" },
                        { label: "活动总数", value: `${selectedTenant.totalActivities} 场` },
                        { label: "存储使用", value: `${selectedTenant.storageUsed} MB` },
                        { label: "开通时间", value: selectedTenant.createdAt },
                        { label: "到期时间", value: selectedTenant.expiresAt },
                        { label: "最后活跃", value: selectedTenant.lastActive },
                      ].map((item) => (
                        <div key={item.label} className="flex justify-between">
                          <span className="text-gray-400">{item.label}</span>
                          <span className="text-gray-700 font-medium text-right max-w-[55%] truncate">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === "features" && (
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-3">
                      {mergedByPlan[selectedTenant.plan].label} 套餐功能
                    </p>
                    <div className="space-y-2">
                      {[
                        "会员管理", "内容发布", "活动管理", "投票管理",
                        "消息通知", "数据报表", "系统设置", "API接入", "定制开发",
                      ].map((feat) => {
                        const planFeats = mergedByPlan[selectedTenant.plan].features;
                        const enabled = planFeats.includes(feat) || planFeats.includes("全部功能");
                        return (
                          <div key={feat} className={`flex items-center justify-between p-2.5 rounded-xl ${enabled ? "bg-green-50" : "bg-gray-50"}`}>
                            <span className={`text-xs ${enabled ? "text-gray-700" : "text-gray-400"}`}>{feat}</span>
                            <i className={`text-sm ${enabled ? "ri-check-circle-fill text-green-500" : "ri-close-circle-line text-gray-300"}`}></i>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-400 mt-3 text-center">升级套餐可解锁更多功能</p>

                    <div className="mt-5 pt-4 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-700 mb-2">租户功能开关</p>
                      <p className="text-[11px] text-gray-400 leading-relaxed mb-3">
                        以下权限由运营平台单独控制，不受套餐模板限制。
                      </p>
                      <div
                        className={`flex items-center justify-between p-3 rounded-xl border ${
                          selectedTenant.activityAiCreateEnabled
                            ? "border-emerald-200 bg-emerald-50"
                            : "border-gray-100 bg-gray-50"
                        }`}
                      >
                        <div className="min-w-0 pr-3">
                          <p className="text-xs font-semibold text-gray-800">AI 创建活动</p>
                          <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">
                            开启后，具备活动编辑权的管理员可在小程序活动管理页看到 AI 创建浮动入口。
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={savingFeatureFlag}
                          onClick={() =>
                            void handleToggleActivityAiCreate(!selectedTenant.activityAiCreateEnabled)
                          }
                          className={`relative h-7 w-12 rounded-full flex-shrink-0 transition-colors cursor-pointer disabled:opacity-50 ${
                            selectedTenant.activityAiCreateEnabled ? "bg-emerald-500" : "bg-gray-300"
                          }`}
                          aria-pressed={Boolean(selectedTenant.activityAiCreateEnabled)}
                        >
                          <span
                            className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${
                              selectedTenant.activityAiCreateEnabled ? "translate-x-5" : "translate-x-0.5"
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "ops" && (
                  <div className="space-y-2">
                    <button className="w-full flex items-center gap-2 p-3 bg-amber-50 text-amber-700 rounded-xl hover:bg-amber-100 cursor-pointer transition-colors text-xs font-medium whitespace-nowrap">
                      <i className="ri-refresh-line"></i> 续费 / 升级套餐
                    </button>
                    <button className="w-full flex items-center gap-2 p-3 bg-gray-50 text-gray-700 rounded-xl hover:bg-gray-100 cursor-pointer transition-colors text-xs font-medium whitespace-nowrap">
                      <i className="ri-mail-send-line"></i> 发送通知给管理员
                    </button>
                    <button
                      onClick={() => void handleEnterTenant(selectedTenant)}
                      className="w-full flex items-center gap-2 p-3 bg-amber-500 text-white rounded-xl hover:bg-amber-400 cursor-pointer transition-colors text-xs font-medium whitespace-nowrap"
                    >
                      <i className="ri-login-circle-line"></i> 进入该租户后台
                    </button>
                    <button
                      type="button"
                      disabled={weappQr.busy && weappQr.tenant?.id === selectedTenant.id}
                      onClick={() => void handleGenerateTenantWeappQr(selectedTenant)}
                      className="w-full flex items-center justify-center gap-2 p-3 bg-white border border-gray-200 text-gray-800 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors text-xs font-medium whitespace-nowrap disabled:opacity-50"
                    >
                      <i className="ri-qr-code-line"></i> 生成租户微信小程序码
                    </button>

                    {/* reset password */}
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-400 mb-2">账号安全</p>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => openResetPasswordConfirm(selectedTenant)}
                        className="w-full flex items-center justify-center gap-2 p-3 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 cursor-pointer transition-colors text-xs font-medium whitespace-nowrap disabled:opacity-50"
                      >
                        <i className={`${resettingPassword ? "ri-loader-4-line animate-spin" : "ri-key-2-line"}`} />
                        {resettingPassword ? "正在重置…" : "重置超级管理员密码"}
                      </button>
                    </div>

                    <div className="pt-2 border-t border-gray-100">
                      {selectedTenant.status === "pending" && (
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => void handleToggleStatus(selectedTenant, "activate")}
                          className="w-full flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-xl hover:bg-green-100 cursor-pointer transition-colors text-xs font-medium whitespace-nowrap mb-2 disabled:opacity-50"
                        >
                          <i className="ri-checkbox-circle-line"></i> 立即激活租户
                        </button>
                      )}
                      {selectedTenant.status !== "suspended" ? (
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => setConfirmAction({ tenant: selectedTenant, action: "suspend" })}
                          className="w-full flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 cursor-pointer transition-colors text-xs font-medium whitespace-nowrap disabled:opacity-50"
                        >
                          <i className="ri-forbid-line"></i> 停用此租户
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => void handleToggleStatus(selectedTenant, "activate")}
                          className="w-full flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-xl hover:bg-green-100 cursor-pointer transition-colors text-xs font-medium whitespace-nowrap disabled:opacity-50"
                        >
                          <i className="ri-checkbox-circle-line"></i> 重新激活租户
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirm reset password dialog */}
      {confirmResetPassword && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl p-6 w-96 max-w-[calc(100vw-2rem)]">
            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
              <i className="ri-key-2-line text-blue-600 text-xl"></i>
            </div>
            <h3 className="font-bold text-gray-800 mb-2">确认重置超级管理员密码？</h3>
            <p className="text-sm text-gray-500 mb-5 leading-relaxed">
              将为租户 <strong className="text-gray-700">{confirmResetPassword.name}</strong> 生成新的协会后台登录密码。旧密码立即失效，新密码仅在下一步弹窗中展示一次，请妥善保存。
            </p>
            {resetPasswordError && (
              <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700">
                <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />
                <span>{resetPasswordError}</span>
              </div>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setConfirmResetPassword(null);
                  setResetPasswordError(null);
                }}
                disabled={resettingPassword}
                className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                disabled={resettingPassword}
                onClick={() => void handleResetPassword(confirmResetPassword)}
                className="flex-1 py-2.5 text-sm text-white bg-blue-600 rounded-xl hover:bg-blue-700 cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {resettingPassword ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    重置中…
                  </>
                ) : (
                  "确认重置"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm suspend dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-96">
            <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center mb-4">
              <i className="ri-forbid-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-gray-800 mb-2">确认停用租户？</h3>
            <p className="text-sm text-gray-500 mb-5">
              停用后，<strong className="text-gray-700">{confirmAction.tenant.name}</strong> 的所有用户将无法登录后台，但数据不会丢失。
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmAction(null)} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button type="button" disabled={saving} onClick={() => void handleToggleStatus(confirmAction.tenant, "suspend")} className="flex-1 py-2.5 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap disabled:opacity-50">确认停用</button>
            </div>
          </div>
        </div>
      )}

      {/* Batch confirm dialog */}
      {batchConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-96">
            <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center mb-4">
              <i className="ri-checkbox-multiple-line text-green-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-gray-800 mb-2">批量激活确认</h3>
            <p className="text-sm text-gray-500 mb-1">
              即将激活 <strong className="text-gray-800">{batchSelected.size}</strong> 个租户，激活后这些租户的管理员将可以正常登录后台。
            </p>
            <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mb-5">
              注意：批量激活不会生成凭证卡，请确保各租户管理员已有登录密码或另行通知。
            </p>
            <div className="flex gap-3">
              <button onClick={() => setBatchConfirm(false)} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button type="button" disabled={saving} onClick={() => void handleBatchActivate()} className="flex-1 py-2.5 text-sm text-white bg-green-500 rounded-xl hover:bg-green-400 cursor-pointer whitespace-nowrap disabled:opacity-50">确认激活</button>
            </div>
          </div>
        </div>
      )}

      {/* Tenant mini-program QR (platform) */}
      {weappQr.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-[420px] max-w-[92vw]">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="font-bold text-gray-800">租户微信小程序码</h3>
                <p className="text-xs text-gray-500 mt-1 leading-snug">
                  {weappQr.tenant ? `${weappQr.tenant.name}（${weappQr.tenant.id}）` : ""}
                  {weappQr.url ? " · 下方预览为含协会名称的合成海报（与小程序推广海报风格一致）。" : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={closeWeappQrModal}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {weappQr.err && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2 mb-3">{weappQr.err}</div>
            )}

            <div className="flex flex-col items-center justify-center min-h-[220px] bg-gray-50 rounded-2xl border border-gray-100 px-2 py-3">
              {weappQr.busy ? (
                <div className="flex flex-col items-center gap-2 py-10">
                  <i className="ri-loader-4-line text-2xl text-amber-500 animate-spin"></i>
                  <p className="text-xs text-gray-500">正在生成小程序码…</p>
                </div>
              ) : weappQr.url ? (
                <div className="flex flex-col items-center gap-2 w-full">
                  {weappQr.posterBusy ? (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <i className="ri-loader-4-line animate-spin" />
                      正在合成海报预览…
                    </p>
                  ) : null}
                  <img
                    src={weappQr.posterUrl || weappQr.url}
                    alt={weappQr.posterUrl ? "租户推广海报" : "租户小程序码"}
                    className={
                      weappQr.posterUrl
                        ? "max-h-[380px] w-auto max-w-full rounded-xl border border-gray-200 bg-white shadow-sm object-contain"
                        : "w-[220px] h-[220px] object-contain"
                    }
                  />
                </div>
              ) : (
                <p className="text-xs text-gray-400 py-10">暂无图片</p>
              )}
            </div>

            {!weappQr.busy && weappQr.url && (
              <div className="mt-3 space-y-2">
                <div className="text-[11px] text-gray-500 break-all">
                  <div>
                    <span className="text-gray-400">page：</span>
                    {weappQr.page || "-"}
                  </div>
                  <div>
                    <span className="text-gray-400">scene：</span>
                    {weappQr.scene || "-"}
                  </div>
                  <div>
                    <span className="text-gray-400">env：</span>
                    {weappQr.envVersion || "-"}
                  </div>
                  <div className="text-gray-400 mt-1">「复制图片链接」为服务端返回的原始小程序码 URL（不含标题合成）。</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      setWeappQrSaveTip(null);
                      try {
                        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(weappQr.url);
                      } catch {
                        // ignore
                      }
                    }}
                    className="flex-1 min-w-[108px] py-2.5 text-xs text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap flex items-center justify-center text-center"
                  >
                    复制图片链接
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      setWeappQrSaveTip(null);
                      const rawUrl = weappQr.url;
                      const tid = (weappQr.tenant?.id ?? "tenant").replace(/[^\w.-]+/g, "_");
                      const tenantName = (weappQr.tenant?.name ?? "").trim() || "协会";
                      if (!rawUrl) return;
                      try {
                        let blob: Blob | null = null;
                        let usedPoster = false;
                        if (weappQr.posterUrl) {
                          blob = await dataUrlToBlob(weappQr.posterUrl);
                          usedPoster = true;
                        } else {
                          try {
                            const dataUrl = await createTenantWeappPosterDataUrl(rawUrl, tenantName);
                            blob = await dataUrlToBlob(dataUrl);
                            usedPoster = true;
                          } catch {
                            blob = null;
                          }
                        }
                        if (!blob) {
                          const res = await fetch(rawUrl, { mode: "cors" });
                          if (!res.ok) throw new Error(String(res.status));
                          blob = await res.blob();
                          usedPoster = false;
                        }
                        const objectUrl = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = objectUrl;
                        a.download = usedPoster ? `weapp-poster-${tid}.png` : `weapp-qrcode-${tid}.png`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(objectUrl);
                      } catch {
                        setWeappQrSaveTip(
                          "当前环境无法直接下载（多为跨域限制）。请点击「新窗口打开」，在图片上右键选择「图片另存为」。",
                        );
                      }
                    }}
                    className="flex-1 min-w-[108px] py-2.5 text-xs text-gray-800 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center justify-center text-center gap-1"
                  >
                    <i className="ri-download-2-line" />
                    {weappQr.posterUrl || weappQr.posterBusy ? "保存海报" : "保存图片"}
                  </button>
                  <a
                    href={weappQr.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 min-w-[108px] py-2.5 text-xs text-white bg-amber-500 rounded-xl hover:bg-amber-400 flex items-center justify-center text-center whitespace-nowrap"
                  >
                    新窗口打开
                  </a>
                </div>
                {weappQrSaveTip && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">{weappQrSaveTip}</p>
                )}
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={closeWeappQrModal}
                className="px-5 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onboard Modal */}
      {showOnboard && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-[580px] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-gray-800">开通新租户</h3>
                <p className="text-xs text-gray-400 mt-0.5">填写基本信息，系统将自动生成租户代码与初始登录凭证</p>
              </div>
              <button onClick={() => setShowOnboard(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">组织全称 *</label>
                  <input type="text" value={newTenant.name} onChange={(e) => setNewTenant((p) => ({ ...p, name: e.target.value }))} placeholder="如：XX球迷协会" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">组织简称</label>
                  <input type="text" value={newTenant.shortName} onChange={(e) => setNewTenant((p) => ({ ...p, shortName: e.target.value }))} placeholder="如：XX FC" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">管理员姓名</label>
                  <input type="text" value={newTenant.adminName} onChange={(e) => setNewTenant((p) => ({ ...p, adminName: e.target.value }))} placeholder="联系人姓名" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">管理员邮箱 *</label>
                  <input type="email" value={newTenant.adminEmail} onChange={(e) => setNewTenant((p) => ({ ...p, adminEmail: e.target.value }))} placeholder="admin@example.com" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">管理员电话</label>
                  <input type="tel" value={newTenant.adminPhone} onChange={(e) => setNewTenant((p) => ({ ...p, adminPhone: e.target.value }))} placeholder="如：138 0000 0000" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">所在地区（省 / 市 / 区县可选）</label>
                  <ChinaRegionPicker
                    value={newTenant.region}
                    onChange={(region: ChinaRegionValue) => setNewTenant((p) => ({ ...p, region }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">运动类型</label>
                  <select value={newTenant.sport} onChange={(e) => setNewTenant((p) => ({ ...p, sport: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white">
                    {["足球", "篮球", "排球", "棒球", "网球", "其他"].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-4">选择套餐</label>
                <div className="grid grid-cols-2 gap-2">
                  {(["trial", "basic", "standard", "premium"] as TenantPlan[]).map((plan) => {
                    const cfg = mergedByPlan[plan];
                    return (
                      <button
                        key={plan}
                        onClick={() => setNewTenant((p) => ({ ...p, plan }))}
                        className={`text-left p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          newTenant.plan === plan ? `border-amber-400 ${cfg.bgColor}` : "border-gray-100 bg-gray-50 hover:border-gray-200"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-sm font-semibold ${newTenant.plan === plan ? cfg.color : "text-gray-700"}`}>{cfg.label}</span>
                          <span className="text-xs text-gray-500">{cfg.price > 0 ? `¥${cfg.price.toLocaleString()}/年` : "免费"}</span>
                        </div>
                        <p className="text-xs text-gray-400">
                          {cfg.maxMembers >= 999999 ? "无限" : cfg.maxMembers.toLocaleString()} 会员 · {cfg.storage >= 10000 ? "10GB+" : `${cfg.storage}MB`}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">到期时间</label>
                <input type="date" value={newTenant.expiresAt} onChange={(e) => setNewTenant((p) => ({ ...p, expiresAt: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">微信支付子商户号（可选）</label>
                <input
                  type="text"
                  value={newTenant.weChatPaySubMchId}
                  onChange={(e) => setNewTenant((p) => ({ ...p, weChatPaySubMchId: e.target.value }))}
                  placeholder="mchid，协会开启在线支付入会时使用"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-amber-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">上级租户（可选）</label>
                <p className="text-[11px] text-gray-400 mb-1.5">默认无上级；仅活动报名可与上下级互通。</p>
                <select
                  value={newTenant.parentTenantId}
                  onChange={(e) => setNewTenant((p) => ({ ...p, parentTenantId: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white"
                >
                  <option value="">无（本租户独立）</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}（{t.id}）
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">登记证书编号（可选）</label>
                <input
                  type="text"
                  value={newTenant.certNumber}
                  onChange={(e) => setNewTenant((p) => ({ ...p, certNumber: e.target.value }))}
                  placeholder="社会团体法人登记证书编号等"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">团体登记证书图片（可选）</label>
                {newTenant.registrationCertImageUrl.trim() ? (
                  <div className="flex flex-wrap items-start gap-2 mb-2">
                    <img
                      src={newTenant.registrationCertImageUrl.trim()}
                      alt="证书预览"
                      className="max-h-24 rounded-xl border border-gray-200 object-contain bg-gray-50"
                    />
                    <button
                      type="button"
                      onClick={() => setNewTenant((p) => ({ ...p, registrationCertImageUrl: "" }))}
                      className="text-xs text-red-600 px-2 py-1 rounded-lg border border-red-200"
                    >
                      清除
                    </button>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2 mb-2">
                  <label className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs border border-gray-200 bg-gray-50 hover:bg-gray-100 cursor-pointer disabled:opacity-40">
                    <i className="ri-image-add-line" />
                    {certUploadBusy === "onboard" ? "上传中…" : "上传扫描件"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={saving || certUploadBusy !== null}
                      onChange={(e) => void uploadCertImage(e.target.files?.[0] ?? null, "onboard")}
                    />
                  </label>
                </div>
                <input
                  type="url"
                  value={newTenant.registrationCertImageUrl}
                  onChange={(e) => setNewTenant((p) => ({ ...p, registrationCertImageUrl: e.target.value }))}
                  placeholder="或粘贴图片 URL"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-xs font-mono focus:outline-none focus:border-amber-400"
                />
              </div>

              {/* tip */}
              <div className="flex items-center gap-2 bg-green-50 rounded-xl px-3 py-2.5">
                <i className="ri-shield-keyhole-line text-green-500 text-sm"></i>
                <p className="text-xs text-green-700">确认开通后，系统将自动生成初始登录密码并展示凭证卡，请妥善保存。</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
              <button onClick={() => setShowOnboard(false)} className="px-5 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button
                type="button"
                onClick={() => void handleOnboard()}
                disabled={saving || !newTenant.name || !newTenant.adminEmail}
                className="px-5 py-2.5 text-sm text-white bg-amber-500 rounded-xl hover:bg-amber-400 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                确认开通并生成凭证
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credential Card */}
      {credential && (
        <CredentialCard cred={credential} onClose={() => setCredential(null)} />
      )}
    </div>
  );
}
